// Spec 45 component-behavior — cmp_ds_icon_button.
//
// Icon-only button surface. THE distinguishing rule: aria-label is
// REQUIRED — without one, the button has no accessible name and
// screenreaders announce "button" with no indication of its action.
// The Phase 4b ESLint plugin (Phase 4b.X) will flag missing aria-label;
// the component itself enforces at runtime via TS.
//
// Variants: default / ghost / danger (per spec 45 cmp_ds_icon_button).
// Sizes: sm / md / lg.

import { Slot } from "@radix-ui/react-slot";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

import { cx } from "../lib/cx.ts";
import { isDevGuardActive } from "../lib/dev-guard.ts";

export type IconButtonVariant = "default" | "ghost" | "danger";
export type IconButtonSize = "sm" | "md" | "lg";

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "aria-label"> {
  /**
   * Accessible label — REQUIRED. Without this, screenreaders announce
   * just "button" with no indication of what activates. The TS type
   * marks it required; the runtime guard in the component throws in
   * dev mode if absent (so the fix surfaces during dev, not in
   * production a11y audits).
   */
  readonly "aria-label": string;
  readonly variant?: IconButtonVariant;
  readonly size?: IconButtonSize;
  readonly loading?: boolean;
  readonly type?: "button" | "submit" | "reset";
  readonly asChild?: boolean;
  /** The icon component (lucide / phosphor / inline svg). */
  readonly children?: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      "aria-label": ariaLabel,
      variant = "default",
      size = "md",
      loading = false,
      type = "button",
      asChild = false,
      disabled,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    if (isDevGuardActive() && (typeof ariaLabel !== "string" || ariaLabel.trim().length === 0)) {
      // Throw loud in dev — silent passes are how a11y debt accrues.
      throw new Error(
        "<IconButton> requires a non-empty aria-label (icon-only buttons have no visible text for screenreaders to announce).",
      );
    }
    const Comp = asChild ? Slot : "button";
    const effectiveDisabled = disabled === true || loading;
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        aria-label={ariaLabel}
        data-component="icon-button"
        data-variant={variant}
        data-size={size}
        data-loading={loading || undefined}
        disabled={asChild ? undefined : disabled === true && !loading ? true : undefined}
        aria-disabled={effectiveDisabled || undefined}
        aria-busy={loading || undefined}
        className={cx("nai-icon-btn", className)}
        {...rest}
      >
        {children}
      </Comp>
    );
  },
);

IconButton.displayName = "IconButton";
